import { useEffect, useState, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetsContext } from '../../../../GlobalContex';
import useSelectedProduct from './hooks/useSelectedProduct';
import useNotifications from '../../../../hooks/useNotifications';
import useFileChecker from './hooks/useFileChecker';
import ProductEditor from './Screens/ProductEditor';
import ProductsList from './Screens/ProductsList';
import ShopSettings from './Screens/ShopSettings';
import useProducts from './hooks/useProducts';
import useEvents from './hooks/useEvents';
import useShop from './hooks/useShop';
import { Row } from 'react-bootstrap';
import TopBar from '../../../builder/Interface/TopBar/TopBar';
import Loader from '../../../builder/Interface/Loader/Loader';
import styles from './Shop.module.css';

const MAX_IMAGE_FILE_SIZE = process.env.REACT_APP_SHOP_PRODUCT_IMAGE_MAX_FILE_SIZE;
const ALLOWED_IMAGE_EXTENSIONS = process.env.REACT_APP_SHOP_PRODUCT_IMAGE_ALLOWED_EXTENSIONS;

const Screen = {
  SHOP_SETTINGS: 0,
  PRODUCTS_LIST: 1,
  PRODUCT_EDITOR: 2
};

const ShopConnectButton = () => {
  const { t } = useTranslation();
  const { subscribe } = useEvents();
  const checkImageFile = useFileChecker({ size: MAX_IMAGE_FILE_SIZE, extnames: ALLOWED_IMAGE_EXTENSIONS });
  const [page, setPage] = useState(1);
  const [screen, setScreen] = useState(Screen.PRODUCTS_LIST);
  const { setNotifications } = useNotifications();
  const { activeWidget, remoteWidget } = useContext(WidgetsContext);
  const shop = useShop(activeWidget);
  const products = useProducts(shop, { pagination: { page, perPage: 10 } });
  const selectedProduct = useSelectedProduct(products);

  const openShopSettings = useCallback(() => setScreen(Screen.SHOP_SETTINGS), []);
  const openProductsList = useCallback(() => setScreen(Screen.PRODUCTS_LIST), []);
  const openProductEditor = useCallback((product) => {
    selectedProduct.current = product;
    setScreen(Screen.PRODUCT_EDITOR);
  }, []);

  useEffect(() => setPage(1), [shop.id]);

  useEffect(() => {
    return subscribe((event, data, meta) => {
      switch (event) {
        case 'error':
          setNotifications((state) => {
            return [{ variant: 'danger', message: data.message }, ...state];
          });
          break;
        case 'shop.settings.updated':
          remoteWidget.set({ shop: data });
          break;
        case 'shop.products.outdated':
          products.refresh();
          remoteWidget.refreshProductsList();
          break;
        case 'shop.products.updated':
          if (meta.changes && 'status' in meta.changes) {
            remoteWidget.refreshProductsList();
          } else {
            remoteWidget.updateProduct(data);
          }
          break;
        case 'shop.products.empty':
          if (meta.currentPage > 1) {
            setPage(meta.totalPages);
          }
          break;
      }
    });
  }, [subscribe, products.refresh, remoteWidget]);

  const addProduct = useCallback(async () => {
    const product = await products.add();
    product && openProductEditor(product);
  }, [products.add, openProductEditor]);

  const updateProductDefaultImage = useCallback((imageFile) => {
    try {
      checkImageFile(imageFile);
      selectedProduct.updateDefaultImage(imageFile);
    } catch (err) {
      setNotifications((state) => [{ variant: 'danger', message: err.message }, ...state]);
    }
  }, [checkImageFile, selectedProduct.updateDefaultImage]);

  return (
    <div
      className={`bg-white ${screen === Screen.PRODUCT_EDITOR ? styles.rmButtonFlap : ''}`}
      style={{ position: 'relative' }}
    >
      <div style={{ marginTop: '-15px', marginLeft: '-15px', marginRight: '-15px' }}>
        {screen === Screen.PRODUCTS_LIST ? (
          <TopBar withoutIcon={true}>{t('Shop widget')}</TopBar>
        ) : (
          <TopBar onClick={openProductsList}>{t('Product List')}</TopBar>
        )}
      </div>
      {(shop.isLoading || products.isLoading) && (
        <div
          style={{
            height: '100%',
            width: '100%',
            position: 'absolute',
            background: 'rgb(255,255,255)',
            opacity: 0.5,
            zIndex: 1
          }}
        >
          <Loader />
        </div>
      )}
      <Row className={styles.shop} style={{ position: 'relative', minHeight: '100px' }}>
        {
          /** Products List */
          screen === Screen.PRODUCTS_LIST && (
            <ProductsList
              products={products.list}
              pagination={products.pagination}
              onPageChange={setPage}
              onProductAdd={addProduct}
              onProductSelect={openProductEditor}
              onProductDelete={products.delete}
              onShopSettingsOpen={openShopSettings}
            ></ProductsList>
          )
        }
        {
          /** Product Editor */
          screen === Screen.PRODUCT_EDITOR && selectedProduct.current && !shop.isLoading && (
            <ProductEditor
              shop={shop}
              product={selectedProduct.current}
              onChange={selectedProduct.update}
              onImageDelete={selectedProduct.deleteImage}
              onImageFileChange={updateProductDefaultImage}
            ></ProductEditor>
          )
        }
        {
          /** Shop Settings */
          screen === Screen.SHOP_SETTINGS && (
            <ShopSettings
              onChange={shop.updateSettings}
              settings={shop.settings}
              currencies={shop.currencies}
            ></ShopSettings>
          )
        }
      </Row>
    </div>
  );
};

export default ShopConnectButton;
